import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Eye, Lightbulb, Users, Globe, TrendingUp, Linkedin } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' } }),
};

const goals = [
  { year: '2025', title: 'BioSleeve Prototype', desc: 'Complete clinical-grade BioSleeve prototype and initiate pilot rehabilitation trials.' },
  { year: '2026', title: 'Regulatory Approval', desc: 'Obtain CE Mark approval for BioSleeve medical device classification in the EU & UK.' },
  { year: '2027', title: 'NanoMed Phase I', desc: 'Begin Phase I safety trials for NanoMed Delivery System nanocarrier platform.' },
  { year: '2028', title: 'Global Expansion', desc: 'Scale operations across Europe, North America, and South-East Asia.' },
];

const values = [
  { icon: Target, title: 'Precision', desc: 'Every solution we build is engineered for exactness — at the biological and the engineering level.' },
  { icon: Eye, title: 'Transparency', desc: 'We publish our research, engage with ethics, and build trust through openness.' },
  { icon: Lightbulb, title: 'Innovation', desc: 'We challenge the boundaries of what biology and technology can achieve together.' },
  { icon: Users, title: 'Accessibility', desc: 'Our technologies are designed from the start to reach those who need them most.' },
];

export default function AboutPage() {
  return (
    <main className="pt-24">
      {/* ── Page Hero ── */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 10% 30%, rgba(0,240,255,0.07) 0%, transparent 60%)' }}
        />
        <div className="max-w-7xl relative z-10">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="section-label">About Us</span>
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="font-outfit font-bold text-5xl sm:text-6xl text-white mt-6 mb-6 leading-tight"
            data-testid="about-heading"
          >
            The Architects of<br />
            <span className="text-gradient-cyan">Tomorrow's Biology</span>
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-white/55 font-inter-tight text-lg max-w-2xl leading-relaxed"
          >
            We're a team of scientists, engineers, and visionaries based in India — working at the frontier of what's biologically and technologically possible.
          </motion.p>
        </div>
      </section>

      {/* ── Vision & Mission ── */}
      <section className="px-6 md:px-12 lg:px-24 py-16 border-t border-bvx-border">
        <div className="max-w-7xl grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="glass-card p-8 card-hover"
            data-testid="vision-card"
          >
            <div className="w-10 h-10 rounded-lg bg-bvx-cyan/10 border border-bvx-cyan/20 flex items-center justify-center mb-5">
              <Eye size={18} className="text-bvx-cyan" />
            </div>
            <span className="font-rajdhani uppercase tracking-widest text-bvx-cyan text-xs block mb-3">Vision</span>
            <h3 className="font-outfit font-bold text-white text-2xl mb-4">A Seamlessly Integrated Future</h3>
            <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
              A world where technology and biology integrate seamlessly — enabling humans to transcend biological limitations and live healthier, longer, more fulfilling lives. We envision a future where disease detection happens before symptoms appear, where mobility is restored not reduced, and where human capability is limited only by imagination.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2} viewport={{ once: true }}
            className="glass-card p-8 card-hover"
            data-testid="mission-card"
          >
            <div className="w-10 h-10 rounded-lg bg-bvx-green/10 border border-bvx-green/20 flex items-center justify-center mb-5">
              <Target size={18} className="text-bvx-green" />
            </div>
            <span className="font-rajdhani uppercase tracking-widest text-bvx-green text-xs block mb-3">Mission</span>
            <h3 className="font-outfit font-bold text-white text-2xl mb-4">Extending Human Potential</h3>
            <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
              To build technologies that extend human potential — making people healthier, more capable, and more connected to their bodies than ever before. We do this through rigorous research, ethical engineering, and an unwavering commitment to building technologies that serve people first, not profit first.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── The Problem ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-bvx-border">
        <div className="max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <span className="section-label">The Problem We Solve</span>
              <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 mb-6 leading-tight">
                Humanity Deserves<br />
                <span className="text-gradient-cyan">Better Tools</span>
              </h2>
              <p className="text-white/55 font-inter-tight text-base leading-relaxed mb-4">
                Every year, millions of people lose motor function due to stroke, neurological disorders, or injury. Current rehabilitation tools are expensive, inaccessible, and frustratingly imprecise. Meanwhile, cancer treatments blast the body with toxins when molecular precision is achievable.
              </p>
              <p className="text-white/55 font-inter-tight text-base leading-relaxed mb-4">
                The gap between what's scientifically possible and what patients actually receive is vast. Biovex exists to close that gap — bringing laboratory-grade precision into the hands of clinicians, patients, and individuals who need it.
              </p>
              <p className="text-white/55 font-inter-tight text-base leading-relaxed">
                We don't just build products. We build solutions that directly address the most pressing failures of modern medicine and human performance technology.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2} viewport={{ once: true }}
              className="glass-card p-8 relative overflow-hidden"
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse 80% 80% at 50% 50%, rgba(57,255,20,0.04) 0%, transparent 70%)' }}
              />
              <div className="relative z-10 space-y-6">
                {[
                  { stat: '15M+', desc: 'Stroke survivors worldwide with motor impairment', color: 'text-bvx-cyan' },
                  { stat: '9.6M', desc: 'Cancer deaths annually from non-targeted treatment', color: 'text-bvx-green' },
                  { stat: '80%', desc: 'Of disability tech inaccessible to low-income patients', color: 'text-yellow-400' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <p className={`font-outfit font-bold text-3xl ${item.color} w-20 shrink-0`}>{item.stat}</p>
                    <p className="text-white/55 font-inter-tight text-sm leading-relaxed pt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">Core Values</span>
            <h2 className="font-outfit font-bold text-4xl text-white mt-6 leading-tight">
              What We Stand For
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                variants={fadeUp} initial="hidden" whileInView="visible" custom={i * 0.1} viewport={{ once: true }}
                className="glass-card p-6 card-hover"
                data-testid={`value-card-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-bvx-cyan/10 border border-bvx-cyan/20 flex items-center justify-center mb-4">
                  <v.icon size={18} className="text-bvx-cyan" />
                </div>
                <h3 className="font-outfit font-semibold text-white text-base mb-2">{v.title}</h3>
                <p className="text-white/45 text-xs font-inter-tight leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Founder ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">Leadership</span>
            <h2 className="font-outfit font-bold text-4xl text-white mt-6">Founder Profile</h2>
          </motion.div>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="glass-card p-8 md:p-10 max-w-3xl"
            data-testid="founder-card"
          >
            <div className="flex items-start gap-6">
              <div className="shrink-0">
                <img
                  src="https://miro.medium.com/v2/resize:fill:176:176/1*Iw14-yKgFAHSSfPB3XotuA@2x.jpeg"
                  alt="Shreyansh Goswami"
                  className="w-16 h-16 rounded-2xl object-cover border border-bvx-cyan/30"
                />
              </div>
              <div>
                <h3 className="font-outfit font-bold text-white text-xl">Shreyansh Goswami</h3>
                <p className="text-bvx-cyan text-sm font-inter-tight mt-1">Founder & CEO</p>
                <a
                  href="https://www.linkedin.com/in/shreyansh-goswami-7b3a15378"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-bvx-cyan transition-colors duration-200 mt-1"
                  data-testid="founder-linkedin"
                >
                  <Linkedin size={11} /> LinkedIn Profile
                </a>
                <div className="mt-4 space-y-3">
                  <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                    Shreyansh Goswami is the Founder and CEO of Biovex Technologies — a visionary at the intersection of medicine, biotech, and advanced engineering. Deeply passionate about healthcare innovation, patient care, and the ethical dimensions of modern medicine.
                  </p>
                  <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                    A student of medicine with a keen interest in ethics, evidence-based practice, and health-tech — Shreyansh founded Biovex with a singular purpose: to build technologies that actually reach and transform people's lives, bridging the gap between cutting-edge research and real patient outcomes.
                  </p>
                  <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                    Based in India, with a global perspective on healthcare innovation and technology-driven solutions for the next generation of medical challenges.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">The Team</span>
            <h2 className="font-outfit font-bold text-4xl text-white mt-6">Advisors &amp; Partners</h2>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="glass-card p-8 md:p-10 max-w-3xl"
            data-testid="advisor-vyas-card"
          >
            <div className="flex items-start gap-6">
              <div className="shrink-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_biotech-labs-1/artifacts/u8e46id2_WhatsApp%20Image%202026-03-28%20at%2016.34.29.jpeg"
                  alt="Vyas Srinivas"
                  className="w-20 h-20 rounded-2xl object-cover border border-bvx-green/30"
                />
              </div>
              <div>
                <h3 className="font-outfit font-bold text-white text-xl">Vyas Srinivas</h3>
                <p className="text-bvx-green text-sm font-inter-tight mt-1">Business Model Advisor</p>
                <div className="mt-4 space-y-3">
                  <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                    Vyas has worked in the fields of Architecture, Sales, Marketing, and Software Consulting for more than 13 years. He's a seasoned Consultant with more than 8 years of experience in leading Software companies like Freshworks and MoEngage.
                  </p>
                  <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                    He's served diverse industries across Digital Marketing, E-commerce, Editorial, and Business Development. He currently heads Strategy and Growth at JCCCD, and works on new-age careers, projects, tech initiatives, and entrepreneurial ideas with the most promising candidates.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 mt-5">
                  {['Freshworks', 'MoEngage', 'Strategy', 'Growth', 'JCCCD'].map((tag) => (
                    <span key={tag} className="text-xs text-white/40 bg-white/5 border border-white/8 px-3 py-1 rounded-full font-inter-tight">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Future Goals / Roadmap ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">Roadmap</span>
            <h2 className="font-outfit font-bold text-4xl text-white mt-6">Future Goals</h2>
          </motion.div>
          <div className="relative">
            <div className="absolute left-16 top-0 bottom-0 w-px bg-bvx-border hidden md:block" />
            <div className="space-y-8">
              {goals.map((g, i) => (
                <motion.div
                  key={g.year}
                  variants={fadeUp} initial="hidden" whileInView="visible" custom={i * 0.1} viewport={{ once: true }}
                  className="flex gap-8 items-start"
                  data-testid={`goal-${i}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-bvx-cyan/10 border border-bvx-cyan/30 flex items-center justify-center shrink-0 relative z-10">
                    <span className="font-rajdhani font-semibold text-bvx-cyan text-sm">{g.year}</span>
                  </div>
                  <div className="glass-card p-6 flex-1 card-hover">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingUp size={14} className="text-bvx-green" />
                      <h3 className="font-outfit font-semibold text-white text-base">{g.title}</h3>
                    </div>
                    <p className="text-white/50 font-inter-tight text-sm leading-relaxed">{g.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="px-6 md:px-12 lg:px-24 py-20 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-8">
            <div>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white">Invest in the Future</h2>
              <p className="text-white/50 font-inter-tight text-sm mt-2">We're open to partnerships, collaborations, and investor conversations.</p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 shrink-0" data-testid="about-cta-btn">
              Connect With Us <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
